import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { Users, Search, MessageCircle, Wallet, Clock, RefreshCw, ChevronLeft, ChevronRight, X, Send, Download, Plus, CheckCircle, SlidersHorizontal as Settings, BarChart3, Tag, ShieldCheck } from '../components/icons';
import { defaultDates } from '../utils/chartDates';
import { customerName, crmNumber, crmDate, states, permissionNames, gregorianDay, dueDate, downloadFile, exportCustomers, statementHtml } from '../utils/crm';
import './AdminCRM.css';
const request = (method, path, data) => api[method]('/api/crm' + path, method === 'get' ? {
  params: data,
  timeout: 30000
} : data);
function Dialog({
  title,
  onClose,
  children,
  wide = false
}) {
  const ref = useRef(null);
  useEffect(() => {
    const trigger = document.activeElement,
      old = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    ref.current?.querySelector('button,input,textarea,select')?.focus();
    const key = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab') {
        const fields = [...ref.current.querySelectorAll('button,input,textarea,select,a[href]')].filter(el => !el.disabled && el.offsetParent !== null),
          first = fields[0],
          last = fields.at(-1);
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener('keydown', key);
    return () => {
      document.body.style.overflow = old;
      document.removeEventListener('keydown', key);
      trigger?.focus();
    };
  }, [onClose]);
  return <div className="crm-modal-layer"><div className="crm-backdrop" onClick={onClose} /><section ref={ref} className={'crm-modal' + (wide ? ' crm-modal--wide' : '')} role="dialog" aria-modal="true" aria-label={title}><header><h2>{title}</h2><button type="button" onClick={onClose} aria-label="بستن"><X size={20} /></button></header><div className="crm-modal-body">{children}</div></section></div>;
}
function Empty({
  text = 'اطلاعاتی برای نمایش وجود ندارد'
}) {
  return <div className="crm-empty"><Users size={32} /><p>{text}</p></div>;
}
const initialFilters = {
  search: '',
  segment: 'all',
  days: 30,
  owner: '',
  tag: '',
  state: '',
  grade: '',
  minRial: '',
  minGold: '',
  sort: 'id',
  direction: 'desc'
};
const tabs = [['customers', 'مشتریان', Users, 'customers'], ['tasks', 'پیگیری‌ها', Clock, 'tasks'], ['compose', 'ارسال پیام', MessageCircle, 'contact'], ['campaigns', 'سوابق ارسال', Send, 'contact'], ['rules', 'اتوماسیون', RefreshCw, 'tasks'], ['reports', 'گزارش‌ها', BarChart3, 'reports'], ['settings', 'تنظیمات', Settings, 'settings']];
export default function AdminCRM() {
  const [statsOpen, setStatsOpen] = useState(() => window.innerWidth > 850);
  const [meta, setMeta] = useState(null),
    [summary, setSummary] = useState({}),
    [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') === 'tasks' ? 'tasks' : 'customers'),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [success, setSuccess] = useState('');
  const [draft, setDraft] = useState(initialFilters),
    [filters, setFilters] = useState(initialFilters),
    [customers, setCustomers] = useState({
      items: [],
      total: 0,
      page: 1
    }),
    [page, setPage] = useState(1),
    [selected, setSelected] = useState([]),
    [segments, setSegments] = useState([]);
  const [detail, setDetail] = useState(null),
    [detailTab, setDetailTab] = useState('overview'),
    [profile, setProfile] = useState({}),
    [note, setNote] = useState(''),
    [taskForm, setTaskForm] = useState(null),
    [tasks, setTasks] = useState([]),
    [taskFilter, setTaskFilter] = useState({
      status: 'all',
      owner: '',
      overdue: new URLSearchParams(window.location.search).get('overdue') === '1' ? '1' : ''
    }),
    [taskPage, setTaskPage] = useState(1),
    [taskMore, setTaskMore] = useState(false),
    [taskEdit, setTaskEdit] = useState(null);
  const [composer, setComposer] = useState({
      title: 'پیام آبشده خرّمی',
      channel: 'chat',
      purpose: 'service',
      template_key: 'general',
      body: ''
    }),
    [preview, setPreview] = useState(null),
    [campaigns, setCampaigns] = useState([]),
    [campaignPage, setCampaignPage] = useState(1),
    [campaignDetail, setCampaignDetail] = useState(null),
    [rules, setRules] = useState([]),
    [ruleForm, setRuleForm] = useState({
      name: '',
      segment: 'new',
      days: 7,
      owner_id: ''
    }),
    [permissions, setPermissions] = useState([]);
  const defaults = useRef(defaultDates()),
    [dates, setDates] = useState(defaults.current),
    [report, setReport] = useState(null),
    [statement, setStatement] = useState(null),
    [segmentName, setSegmentName] = useState('');
  const mounted = useRef(true),
    listVersion = useRef(0),
    detailVersion = useRef(0),
    actionBusy = useRef(false),
    sendKey = useRef(null);
  const can = key => Boolean(meta?.permissions?.[key]);
  const act = async work => {
    if (actionBusy.current) return;
    actionBusy.current = true;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await work();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'عملیات ناموفق بود');
    } finally {
      actionBusy.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const loadCustomers = useCallback(async () => {
    const version = ++listVersion.current;
    try {
      const {
        data
      } = await request('get', '/customers', {
        ...filters,
        page
      });
      if (mounted.current && version === listVersion.current) setCustomers(data);
    } catch (e) {
      if (mounted.current && version === listVersion.current) setError(e.response?.data?.message || 'دریافت مشتریان ناموفق بود');
    }
  }, [filters, page]);
  const loadTasks = useCallback(async () => {
    try {
      const {
        data
      } = await request('get', '/tasks', {
        ...taskFilter,
        page: taskPage
      });
      if (mounted.current) {
        setTasks(data.items);
        setTaskMore(data.hasMore);
      }
    } catch (e) {
      if (mounted.current) setError(e.response?.data?.message || 'دریافت پیگیری‌ها ناموفق بود');
    }
  }, [taskFilter, taskPage]);
  const loadCampaigns = useCallback(async () => {
    try {
      const {
        data
      } = await request('get', '/campaigns', {
        page: campaignPage
      });
      if (mounted.current) setCampaigns(data);
    } catch (e) {
      if (mounted.current) setError(e.response?.data?.message || 'دریافت سوابق ناموفق بود');
    }
  }, [campaignPage]);
  useEffect(() => {
    mounted.current = true;
    let canceled = false;
    request('get', '/meta').then(async ({
      data
    }) => {
      if (canceled) return;
      setMeta(data);
      const first = tabs.find(item => data.permissions[item[3]]);
      if (first) setTab(first[0]);
      if (data.permissions.customers) {
        const [dash, saved] = await Promise.all([request('get', '/dashboard'), request('get', '/segments')]);
        if (!canceled) {
          setSummary(dash.data);
          setSegments(saved.data);
        }
      }
    }).catch(e => {
      if (!canceled) setError(e.response?.data?.message || 'دسترسی CRM یا اتصال را بررسی کنید');
    });
    return () => {
      canceled = true;
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (meta?.permissions.customers && tab === 'customers') loadCustomers();
  }, [meta, tab, loadCustomers]);
  useEffect(() => {
    if (meta?.permissions.tasks && tab === 'tasks') loadTasks();
  }, [meta, tab, loadTasks]);
  useEffect(() => {
    if (meta?.permissions.contact && tab === 'campaigns') {
      loadCampaigns();
      const timer = setInterval(loadCampaigns, 15000);
      return () => clearInterval(timer);
    }
  }, [meta, tab, loadCampaigns]);
  useEffect(() => {
    if (!meta) return;
    if (tab === 'rules' && meta.permissions.tasks) request('get', '/rules').then(r => setRules(r.data)).catch(e => setError(e.response?.data?.message || 'دریافت قواعد ناموفق بود'));
    if (tab === 'settings' && meta.permissions.settings) request('get', '/permissions').then(r => setPermissions(r.data)).catch(e => setError(e.response?.data?.message || 'دریافت دسترسی‌ها ناموفق بود'));
  }, [tab, meta]);
  useEffect(() => {
    setPreview(null);
    sendKey.current = null;
  }, [composer, selected]);
  const closeDetail = useCallback(() => {
      detailVersion.current++;
      setDetail(null);
    }, []),
    closeTask = useCallback(() => setTaskForm(null), []),
    closeTaskEdit = useCallback(() => setTaskEdit(null), []),
    closeCampaign = useCallback(() => setCampaignDetail(null), []),
    closeStatement = useCallback(() => setStatement(null), []);
  const openCustomer = async id => {
    const version = ++detailVersion.current;
    setError('');
    setDetail({
      loading: true,
      customer: {
        id
      }
    });
    setDetailTab('overview');
    try {
      const {
        data
      } = await request('get', `/customers/${id}`);
      if (version !== detailVersion.current || !mounted.current) return;
      setDetail(data);
      setProfile({
        owner_id: data.customer.owner_id || '',
        state: data.customer.crm_state,
        tags: data.customer.tags.join('، '),
        marketing_opt_in: Boolean(Number(data.customer.marketing_opt_in))
      });
      setNote('');
    } catch (e) {
      if (version === detailVersion.current) {
        setDetail(null);
        setError(e.response?.data?.message || 'پرونده دریافت نشد');
      }
    }
  };
  const pick = id => setSelected(current => current.includes(id) ? current.filter(value => value !== id) : [...current, id]);
  const sort = key => {
    setPage(1);
    setFilters(f => ({
      ...f,
      sort: key,
      direction: f.sort === key && f.direction === 'desc' ? 'asc' : 'desc'
    }));
  };
  const newTask = id => {
    closeDetail();
    setTaskForm({
      user_id: id || '',
      title: '',
      body: '',
      owner_id: meta.admins[0]?.id || '',
      priority: 'normal',
      day: defaults.current.end,
      time: '10:00'
    });
  };
  const composeFor = (id, balance = false) => {
    setSelected([id]);
    setComposer(c => ({
      ...c,
      template_key: balance ? 'balance' : 'general',
      body: '',
      title: balance ? 'مانده حساب شما' : 'پیام پشتیبانی'
    }));
    closeDetail();
    setTab('compose');
  };
  const loadReport = () => act(async () => {
    const {
      data
    } = await request('get', '/reports', {
      start: gregorianDay(dates.start),
      end: gregorianDay(dates.end)
    });
    setReport(data);
  });
  const downloadTemplates = () => {
    const lines = meta.templates.map(t => `${t.name}\nشناسه: ${t.provider_id || 'نیازمند ساخت قالب'}\n${t.sms.replace(/\{([A-Z]+)\}/g, '#$1#')}\nپارامترها: ${t.params.join('، ')}\n`);
    downloadFile('crm-sms-templates.txt', lines.join('\n----------------\n'));
  };
  const smsNotReady = composer.channel === 'sms' && (!preview?.smsEnabled || !preview?.providerId);
  const kpis = [['customers', 'مشتریان', 'all', Users], ['new_customers', 'بدون معامله', 'new', Users], ['charged_customers', 'شارژ بدون معامله', 'charged', Wallet], ['inactive_customers', 'غیرفعال ۳۰ روز', 'inactive', Clock], ['balance_customers', 'مانده بدون گردش', 'balance', Wallet], ['debt_customers', 'دارای بدهی', 'debt', Wallet], ['unanswered_customers', 'چت بدون پاسخ', 'unanswered', MessageCircle], ['pending_customers', 'درخواست در انتظار', 'pending', Clock]];
  return <section className="crm" dir="rtl">
  {error && <div className="crm-alert crm-alert--error" role="alert">{error}<button onClick={() => setError('')} aria-label="بستن خطا"><X size={16} /></button></div>}{success && <div className="crm-alert" role="status"><CheckCircle size={18} />{success}</div>}
  {!meta && !error && <p role="status">در حال آماده‌سازی CRM…</p>}
  {meta && <><details className="crm-dashboard" open={statsOpen} onToggle={e => setStatsOpen(e.currentTarget.open)}><summary>نمای کلی مشتریان و پیگیری‌ها · {crmNumber(summary.customers)} مشتری</summary><div className="crm-kpis">{kpis.filter(([key]) => summary[key] !== undefined).map(([key, label, segment, Icon]) => <button key={key} onClick={() => {
            setDraft({
              ...initialFilters,
              segment
            });
            setFilters({
              ...initialFilters,
              segment
            });
            setPage(1);
            setTab('customers');
          }}><span><Icon size={19} />{label}</span><strong>{crmNumber(summary[key])}</strong></button>)}{can('tasks') && <button onClick={() => {
            setTaskFilter(f => ({
              ...f,
              overdue: '1'
            }));
            setTaskPage(1);
            setTab('tasks');
          }}><span><Clock size={19} />پیگیری عقب‌افتاده</span><strong>{crmNumber(summary.overdue)}</strong></button>}</div></details>
  <nav className="crm-tabs" aria-label="بخش‌های CRM">{tabs.filter(item => can(item[3])).map(([key, label, Icon]) => <button key={key} className={tab === key ? 'is-active' : ''} onClick={() => {
          setTab(key);
          setError('');
          setSuccess('');
        }}><Icon size={17} />{label}</button>)}</nav>
  {tab === 'customers' && can('customers') && <><form className="crm-filter" onSubmit={e => {
          e.preventDefault();
          setFilters({
            ...draft
          });
          setPage(1);
        }}><label className="crm-search"><Search size={18} /><input aria-label="جستجوی مشتری" placeholder="نام، شماره همراه، کد سیستم یا شناسه" value={draft.search} onChange={e => setDraft(d => ({
              ...d,
              search: e.target.value
            }))} /></label><label>گروه مشتری<select value={draft.segment} onChange={e => setDraft(d => ({
              ...d,
              segment: e.target.value
            }))}>{Object.entries(meta.segments).filter(([key]) => can('financial') || !['debt', 'balance'].includes(key)).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label><label>بازه روز<input type="number" min="1" max="3650" value={draft.days} onChange={e => setDraft(d => ({
              ...d,
              days: e.target.value
            }))} /></label><label className="crm-mobile-sort">مرتب‌سازی<select value={draft.sort} onChange={e => setDraft(d => ({
              ...d,
              sort: e.target.value
            }))}>{[['id', 'شناسه'], ['name', 'نام'], ['trades', 'تعداد معاملات'], ['last_trade', 'آخرین معامله'], ['followup', 'موعد پیگیری'], ...(can('financial') ? [['rial', 'مانده ریال'], ['gold', 'مانده طلا'], ['volume', 'حجم معاملات']] : [])].map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label><label className="crm-mobile-sort">ترتیب<select value={draft.direction} onChange={e => setDraft(d => ({
              ...d,
              direction: e.target.value
            }))}><option value="desc">نزولی</option><option value="asc">صعودی</option></select></label><button className="crm-primary" disabled={busy}>اعمال فیلتر</button><details><summary>فیلترهای بیشتر</summary><div className="crm-form-grid"><label>مسئول<select value={draft.owner} onChange={e => setDraft(d => ({
                  ...d,
                  owner: e.target.value
                }))}><option value="">همه</option>{meta.admins.map(a => <option key={a.id} value={a.id}>{customerName(a)}</option>)}</select></label><label>برچسب<input value={draft.tag} onChange={e => setDraft(d => ({
                  ...d,
                  tag: e.target.value
                }))} /></label><label>وضعیت<select value={draft.state} onChange={e => setDraft(d => ({
                  ...d,
                  state: e.target.value
                }))}><option value="">همه</option>{['normal', 'new', 'active', 'inactive', 'vip', 'needs_help'].map(key => <option key={key} value={key}>{states[key]}</option>)}</select></label><label>گرید<input value={draft.grade} onChange={e => setDraft(d => ({
                  ...d,
                  grade: e.target.value
                }))} /></label>{can('financial') && <><label>حداقل مانده ریال<input value={draft.minRial} onChange={e => setDraft(d => ({
                    ...d,
                    minRial: e.target.value
                  }))} /></label><label>حداقل مانده طلا<input value={draft.minGold} onChange={e => setDraft(d => ({
                    ...d,
                    minGold: e.target.value
                  }))} /></label></>}</div></details></form>
   <div className="crm-toolbar"><span><strong>{crmNumber(customers.total)}</strong> مشتری · <strong>{crmNumber(selected.length)}</strong> انتخاب‌شده</span><div>{can('bulk') && <button onClick={() => act(async () => {
              const {
                data
              } = await request('get', '/customer-ids', filters);
              setSelected(data);
            })}>انتخاب همه نتایج</button>}<button disabled={!selected.length} onClick={() => setSelected([])}>پاک‌کردن انتخاب</button>{can('contact') && <button className="crm-primary" disabled={!selected.length} onClick={() => setTab('compose')}><Send size={16} />پیام به انتخاب‌شده‌ها</button>}<button onClick={() => exportCustomers(customers.items)}><Download size={16} />خروجی همین صفحه</button><button onClick={loadCustomers} aria-label="تازه‌سازی"><RefreshCw size={16} /></button></div></div>
   <div className="crm-table-wrap"><table className="crm-table crm-customer-table"><thead><tr><th><input type="checkbox" aria-label="انتخاب مشتریان این صفحه" checked={customers.items.length > 0 && customers.items.every(r => selected.includes(r.id))} onChange={e => setSelected(current => e.target.checked ? [...new Set([...current, ...customers.items.map(r => r.id)])] : current.filter(id => !customers.items.some(r => r.id === id)))} /></th><th><button onClick={() => sort('name')}>مشتری ↑↓</button></th>{can('financial') && <><th><button onClick={() => sort('rial')}>مانده ریالی ↑↓</button></th><th><button onClick={() => sort('gold')}>مانده طلایی ↑↓</button></th></>}<th><button onClick={() => sort('trades')}>معامله ↑↓</button></th><th><button onClick={() => sort('last_trade')}>آخرین معامله ↑↓</button></th><th><button onClick={() => sort('followup')}>پیگیری بعدی ↑↓</button></th><th>پرونده</th></tr></thead><tbody>{customers.items.map(row => <tr key={row.id}><td data-label="انتخاب"><input type="checkbox" aria-label={`انتخاب ${customerName(row)}`} checked={selected.includes(row.id)} onChange={() => pick(row.id)} /></td><td data-label="مشتری"><strong>{customerName(row)}</strong><small dir="ltr">{row.phone_number || '—'}</small><small>کد سیستم: {row.system_code || '—'} · گرید {row.grade || '—'}</small><span className="crm-badge">{states[row.crm_state]}</span>{row.tags.map(tag => <span key={tag} className="crm-tag">{tag}</span>)}</td>{can('financial') && <><td data-label="مانده ریالی" className={Number(row.balance_rial) < 0 ? 'crm-debt' : ''}>{crmNumber(row.balance_rial)}<small>قابل استفاده: {crmNumber(row.available_rial)}</small></td><td data-label="مانده طلایی" className={Number(row.balance_gold) < 0 ? 'crm-debt' : ''}>{crmNumber(row.balance_gold, true)}<small>قابل استفاده: {crmNumber(row.available_gold, true)}</small></td></>}<td data-label="معاملات">{crmNumber(row.trades)}</td><td data-label="آخرین معامله">{crmDate(row.last_trade_at)}<small>آخرین حضور: {crmDate(row.last_seen_at)}</small></td><td data-label="پیگیری بعدی">{crmDate(row.next_followup_at)}</td><td data-label="پرونده"><button onClick={() => openCustomer(row.id)}>مشاهده<ChevronLeft size={15} /></button></td></tr>)}</tbody></table></div>{!customers.items.length && <Empty />}
   <div className="crm-footer"><div className="crm-pagination"><button disabled={page === 1} onClick={() => setPage(p => p - 1)}><ChevronRight size={16} />قبلی</button><span>صفحه {crmNumber(page)}</span><button disabled={page * 50 >= customers.total} onClick={() => setPage(p => p + 1)}>بعدی<ChevronLeft size={16} /></button></div><form onSubmit={e => {
            e.preventDefault();
            act(async () => {
              await request('post', '/segments', {
                name: segmentName,
                filters
              });
              setSegmentName('');
              setSegments((await request('get', '/segments')).data);
              setSuccess('گروه پویا ذخیره شد');
            });
          }}><input required placeholder="نام این فیلتر برای ذخیره" value={segmentName} onChange={e => setSegmentName(e.target.value)} /><button disabled={busy}>ذخیره گروه</button></form></div><div className="crm-saved">{segments.map(s => <span key={s.id}><button onClick={() => {
              setDraft({
                ...initialFilters,
                ...s.filters
              });
              setFilters({
                ...initialFilters,
                ...s.filters
              });
              setPage(1);
            }}><Tag size={14} />{s.name}</button><button aria-label={`حذف گروه ${s.name}`} onClick={() => act(async () => {
              await request('delete', `/segments/${s.id}`);
              setSegments(list => list.filter(i => i.id !== s.id));
            })}><X size={14} /></button></span>)}</div>
  </>}
  {tab === 'tasks' && can('tasks') && <><div className="crm-toolbar"><h3>پیگیری‌های مشتریان</h3><button className="crm-primary" onClick={() => newTask()}><Plus size={17} />پیگیری جدید</button></div><div className="crm-filter"><label>وضعیت<select value={taskFilter.status} onChange={e => {
              setTaskPage(1);
              setTaskFilter(f => ({
                ...f,
                status: e.target.value
              }));
            }}>{['all', 'open', 'progress', 'done', 'canceled'].map(key => <option key={key} value={key}>{states[key] || 'همه'}</option>)}</select></label><label>مسئول<select value={taskFilter.owner} onChange={e => {
              setTaskPage(1);
              setTaskFilter(f => ({
                ...f,
                owner: e.target.value
              }));
            }}><option value="">همه</option>{meta.admins.map(a => <option key={a.id} value={a.id}>{customerName(a)}</option>)}</select></label><label className="crm-check"><input type="checkbox" checked={taskFilter.overdue === '1'} onChange={e => {
              setTaskPage(1);
              setTaskFilter(f => ({
                ...f,
                overdue: e.target.checked ? '1' : ''
              }));
            }} />فقط عقب‌افتاده‌ها</label></div><div className="crm-task-list">{tasks.map(t => <article key={t.id}><div><span className={'crm-badge' + (t.priority === 'high' ? ' crm-badge--danger' : '')}>{t.priority === 'high' ? 'فوری' : states[t.status]}</span><h3>{t.title}</h3><button className="crm-text-button" onClick={() => openCustomer(t.user_id)}>{customerName(t)}</button><p>{t.body}</p><small>موعد: {crmDate(t.due_at)} · مسئول: {customerName(meta.admins.find(a => a.id === t.owner_id))}</small>{t.result && <p>نتیجه: {t.result}</p>}</div><button onClick={() => {
              closeDetail();
              setTaskEdit({
                ...t,
                expected_status: t.status,
                result: t.result || '',
                outcome: t.outcome || ''
              });
            }}>مدیریت پیگیری</button></article>)}</div>{!tasks.length && <Empty />}<div className="crm-pagination"><button disabled={taskPage === 1} onClick={() => setTaskPage(p => p - 1)}>قبلی</button><span>{crmNumber(taskPage)}</span><button disabled={!taskMore} onClick={() => setTaskPage(p => p + 1)}>بعدی</button></div></>}
  {tab === 'compose' && can('contact') && <div className="crm-compose"><form onSubmit={e => {
          e.preventDefault();
          act(async () => {
            if (!selected.length) throw Error('ابتدا مشتریان را انتخاب کنید');
            const {
              data
            } = await request('post', '/messages/preview', {
              ...composer,
              user_ids: selected
            });
            setPreview(data);
            sendKey.current = window.crypto.randomUUID();
          });
        }}><h3>پیام تازه برای {crmNumber(selected.length)} مشتری</h3><p>برای تغییر گیرندگان به فهرست مشتریان بروید. هیچ ارسال نهایی بدون پیش‌نمایش انجام نمی‌شود.</p><label>عنوان<input required maxLength={160} value={composer.title} onChange={e => setComposer(c => ({
              ...c,
              title: e.target.value
            }))} /></label><div className="crm-form-grid"><label>مسیر ارسال<select value={composer.channel} onChange={e => setComposer(c => ({
                ...c,
                channel: e.target.value
              }))}><option value="chat">چت پشتیبانی</option><option value="notification">اعلان داخل سایت و پوش</option><option value="sms">پیامک قالبی</option></select></label><label>نوع پیام<select value={composer.purpose} onChange={e => setComposer(c => ({
                ...c,
                purpose: e.target.value
              }))}><option value="service">خدماتی</option><option value="marketing">تبلیغاتی؛ نیازمند رضایت مشتری</option></select></label><label>قالب<select value={composer.template_key} onChange={e => setComposer(c => ({
                ...c,
                template_key: e.target.value
              }))}>{meta.templates.filter(t => t.key !== 'followup' && (t.key !== 'balance' || can('financial'))).map(t => <option key={t.key} value={t.key}>{t.name}</option>)}</select></label></div>{composer.template_key === 'general' && <label>متن پیام<textarea required maxLength={5000} rows={6} value={composer.body} onChange={e => setComposer(c => ({
              ...c,
              body: e.target.value
            }))} /></label>}{composer.template_key === 'balance' && <div className="crm-note">مانده از حساب واقعی خوانده می‌شود و هنگام ارسال دوباره محاسبه می‌شود. زمان محاسبه در پیام و سابقه ثبت خواهد شد.</div>}{composer.channel === 'sms' && <div className="crm-note">هر پارامتر حداکثر ۲۵ کاراکتر. {meta.smsEnabled ? 'ارسال پیامک فعال است.' : 'ارسال واقعی پیامک غیرفعال است؛ پیش‌نمایش قابل استفاده است.'}</div>}{composer.channel === 'sms' && <button type="button" disabled={busy || !meta.smsEnabled || !meta.templates.find(t => t.key === composer.template_key)?.provider_id} onClick={() => act(async () => {
            await request('post', '/messages/test', {
              template_key: composer.template_key,
              body: composer.body
            });
            setSuccess('نمونه با مقدار فرضی به شماره خودتان ارسال و توسط سرویس پذیرفته شد');
          })}>ارسال نمونه به شماره خودم</button>}<button className="crm-primary" disabled={busy || !selected.length}><Search size={17} />پیش‌نمایش گیرندگان و متن</button></form><section className="crm-preview"><h3>پیش‌نمایش ارسال</h3>{preview ? <><p>{crmNumber(preview.eligible)} گیرنده مجاز از {crmNumber(preview.total)}</p>{preview.items.slice(0, 30).map(row => <article key={row.user_id}><strong>{row.name}</strong><small dir="ltr">{row.phone_number}</small><p>{row.body}</p>{row.error && <span className="crm-debt">{row.error}</span>}</article>)}{preview.total > 30 && <p>نمایش ۳۰ نمونه اول؛ اعتبارسنجی همه گیرندگان انجام شده است.</p>}<button className="crm-primary" disabled={busy || preview.eligible !== preview.total || !preview.eligible || smsNotReady} onClick={() => act(async () => {
              const {
                data
              } = await request('post', '/campaigns', {
                ...composer,
                user_ids: selected,
                request_key: sendKey.current
              });
              setSuccess(`ارسال شماره ${data.id} ثبت شد؛ نتیجه در سوابق ارسال قابل پیگیری است`);
              setPreview(null);
              setSelected([]);
              setTab('campaigns');
            })}><Send size={17} />تأیید نهایی و ارسال به {crmNumber(preview.total)} نفر</button></> : <Empty text="ابتدا پیام را آماده و پیش‌نمایش را دریافت کنید" />}</section></div>}
  {tab === 'campaigns' && can('contact') && <><div className="crm-toolbar"><h3>سوابق ارسال فردی و گروهی</h3><button onClick={loadCampaigns}><RefreshCw size={16} />تازه‌سازی</button></div><p className="crm-note">«ثبت در سایت» به معنی ایجاد چت یا اعلان است. «پذیرفته‌شده» تأیید سرویس پیامک است؛ تحویل یا خوانده‌شدن پیامک ادعا نمی‌شود. حالت نامشخص خودکار تکرار نمی‌شود.</p><div className="crm-campaign-list">{campaigns.map(c => <article key={c.id}><div><span className="crm-badge">{states[c.status]}</span><h3>{c.title}</h3><p>{c.channel === 'chat' ? 'چت' : c.channel === 'sms' ? 'پیامک' : 'اعلان'} · {crmNumber(c.recipient_count)} گیرنده · {crmDate(c.created_at)}</p><div className="crm-delivery-counts"><span>موفق: {crmNumber(c.successful)}</span><span>در صف: {crmNumber(c.pending)}</span><span>ناموفق: {crmNumber(c.failed)}</span><span>نامشخص: {crmNumber(c.unknown)}</span></div></div><div><button onClick={() => act(async () => setCampaignDetail({
                campaign: c,
                items: (await request('get', `/campaigns/${c.id}`)).data
              }))}>جزئیات</button>{can('bulk') && Number(c.failed) > 0 && c.status !== 'canceled' && <button disabled={busy} onClick={() => act(async () => {
                await request('post', `/campaigns/${c.id}/retry`, {});
                await loadCampaigns();
                setSuccess('فقط موارد قطعاً ناموفق دوباره در صف قرار گرفتند');
              })}>تکرار ناموفق‌ها</button>}{can('bulk') && c.status === 'queued' && <button disabled={busy} onClick={() => act(async () => {
                await request('post', `/campaigns/${c.id}/cancel`, {});
                await loadCampaigns();
              })}>لغو موارد ارسال‌نشده</button>}</div></article>)}</div>{!campaigns.length && <Empty />}<div className="crm-pagination"><button disabled={campaignPage === 1} onClick={() => setCampaignPage(p => p - 1)}>قبلی</button><span>{crmNumber(campaignPage)}</span><button disabled={campaigns.length < 50} onClick={() => setCampaignPage(p => p + 1)}>بعدی</button></div></>}
  {tab === 'rules' && can('tasks') && <><h3>قواعد پیگیری خودکار</h3><p className="crm-note">قواعد، وظیفه داخلی و هشدار ادمین ایجاد می‌کنند؛ پیام خودکار به مشتری نمی‌فرستند. برای یک وضعیت یکسان، پیگیری تکراری ساخته نمی‌شود.</p>{can('settings') && <form className="crm-filter" onSubmit={e => {
          e.preventDefault();
          act(async () => {
            await request('post', '/rules', ruleForm);
            setRules((await request('get', '/rules')).data);
            setRuleForm(f => ({
              ...f,
              name: ''
            }));
            setSuccess('قاعده غیرفعال ساخته شد؛ پس از بررسی فعال کنید');
          });
        }}><label>نام قاعده<input required value={ruleForm.name} onChange={e => setRuleForm(f => ({
              ...f,
              name: e.target.value
            }))} /></label><label>شرط<select value={ruleForm.segment} onChange={e => setRuleForm(f => ({
              ...f,
              segment: e.target.value
            }))}>{Object.entries(meta.segments).filter(([key]) => !['all', 'active'].includes(key)).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label><label>روز<input type="number" min="1" max="3650" value={ruleForm.days} onChange={e => setRuleForm(f => ({
              ...f,
              days: e.target.value
            }))} /></label><label>مسئول<select required value={ruleForm.owner_id} onChange={e => setRuleForm(f => ({
              ...f,
              owner_id: e.target.value
            }))}><option value="">انتخاب کنید</option>{meta.admins.map(a => <option key={a.id} value={a.id}>{customerName(a)}</option>)}</select></label><button className="crm-primary" disabled={busy}><Plus size={17} />ساخت قاعده</button></form>}<div className="crm-task-list">{rules.map(rule => <article key={rule.id}><div><h3>{rule.name}</h3><p>{meta.segments[rule.segment]} · بازه {crmNumber(rule.days)} روز</p><small>آخرین اجرا: {crmDate(rule.last_run_at)}</small></div>{can('settings') && <button disabled={busy} onClick={() => act(async () => {
              await request('put', `/rules/${rule.id}`, {
                enabled: !Number(rule.enabled)
              });
              setRules((await request('get', '/rules')).data);
            })}>{Number(rule.enabled) ? 'غیرفعال‌کردن' : 'فعال‌کردن'}</button>}</article>)}</div>{!rules.length && <Empty />}{!meta.workerEnabled && <p className="crm-debt">پردازش CRM هنوز فعال نشده است.</p>}</>}
  {tab === 'reports' && can('reports') && <><div className="crm-toolbar"><h3>گزارش ارتباط و پیگیری</h3><div><label>از<input placeholder="۱۴۰۵/۰۷/۰۱" value={dates.start} onChange={e => setDates(d => ({
                ...d,
                start: e.target.value
              }))} /></label><label>تا<input placeholder="۱۴۰۵/۰۷/۳۰" value={dates.end} onChange={e => setDates(d => ({
                ...d,
                end: e.target.value
              }))} /></label><button className="crm-primary" onClick={loadReport} disabled={busy}>دریافت گزارش</button></div></div>{report && <><div className="crm-kpis"><article><span>ثبت‌نام در بازه</span><strong>{crmNumber(report.registrations.registered)}</strong></article><article><span>دارای معامله</span><strong>{crmNumber(report.registrations.converted)}</strong></article><article><span>میانگین زمان پاسخ / دقیقه</span><strong>{report.support.average_reply_seconds == null ? '—' : crmNumber(Number(report.support.average_reply_seconds) / 60)}</strong></article></div><div className="crm-table-wrap"><table className="crm-table"><thead><tr><th>مسئول</th><th>پیگیری</th><th>انجام‌شده</th><th>عقب‌افتاده</th><th>معامله پس از پیگیری</th></tr></thead><tbody>{report.tasks.map(r => <tr key={r.owner_id}><td data-label="مسئول">{customerName(r)}</td><td data-label="پیگیری">{crmNumber(r.total)}</td><td data-label="انجام‌شده">{crmNumber(r.completed)}</td><td data-label="عقب‌افتاده">{crmNumber(r.overdue)}</td><td data-label="معامله بعد پیگیری">{crmNumber(r.traded_after_followup)}</td></tr>)}</tbody></table></div><p className="crm-note">معامله پس از پیگیری، رابطه زمانی را نشان می‌دهد و به معنی اثبات اثر پیگیری نیست. زمان پاسخ فقط برای پیام‌های دارای پاسخ واقعی محاسبه می‌شود.</p><div className="crm-delivery-counts">{report.sending.map(r => <span key={r.state}>{states[r.state]}: {crmNumber(r.count)}</span>)}</div></>}</>}
  {tab === 'settings' && can('settings') && <><div className="crm-toolbar"><h3>قالب‌ها و دسترسی ادمین‌ها</h3><button onClick={downloadTemplates}><Download size={16} />دریافت فهرست قالب‌های پیامک</button></div><div className="crm-settings-grid">{meta.templates.map(t => <form key={t.key} onSubmit={e => {
            e.preventDefault();
            const value = new FormData(e.currentTarget).get('provider_id');
            act(async () => {
              await request('put', `/templates/${t.key}`, {
                provider_id: value
              });
              setMeta((await request('get', '/meta')).data);
              setSuccess('شناسه قالب ذخیره شد');
            });
          }}><h3>{t.name}</h3><pre>{t.sms.replace(/\{([A-Z]+)\}/g, '#$1#')}</pre><label>شناسه قالب<input name="provider_id" defaultValue={t.provider_id || ''} inputMode="numeric" /></label><button disabled={busy}>ذخیره شناسه</button></form>)}</div><p className="crm-note">دسترسی‌های زیر فقط بخش CRM را کنترل می‌کنند. بخش‌های مالی فعلی سایت دسترسی مستقل خود را دارند.</p><div className="crm-permissions">{permissions.map(a => <form key={a.id} onSubmit={e => {
            e.preventDefault();
            act(async () => {
              await request('put', `/permissions/${a.id}`, {
                permissions: a.permissions
              });
              setSuccess('دسترسی CRM ذخیره شد');
            });
          }}><h3><ShieldCheck size={20} />{customerName(a)}</h3>{Object.entries(permissionNames).map(([key, label]) => <label className="crm-check" key={key}><input type="checkbox" checked={Boolean(a.permissions[key])} onChange={e => setPermissions(rows => rows.map(r => r.id === a.id ? {
                ...r,
                permissions: {
                  ...r.permissions,
                  [key]: e.target.checked
                }
              } : r))} />{label}</label>)}<button disabled={busy}>ذخیره دسترسی</button></form>)}</div></>}
  </>}
  {detail && <Dialog title={detail.loading ? 'دریافت پرونده…' : customerName(detail.customer)} onClose={closeDetail} wide>{detail.loading ? <p role="status">در حال دریافت پرونده مشتری…</p> : <><div className="crm-profile-heading"><div><strong dir="ltr">{detail.customer.phone_number}</strong><p>کد سیستم {detail.customer.system_code || '—'} · گرید {detail.customer.grade || '—'} · عضویت {crmDate(detail.customer.created_at)}</p></div><div>{can('contact') && <button className="crm-primary" onClick={() => composeFor(detail.customer.id)}><MessageCircle size={16} />ارسال چت یا پیام</button>}{can('financial') && can('contact') && <button onClick={() => composeFor(detail.customer.id, true)}><Wallet size={16} />ارسال مانده</button>}{can('tasks') && <button onClick={() => newTask(detail.customer.id)}><Plus size={16} />پیگیری</button>}</div></div><nav className="crm-tabs">{[['overview', 'خلاصه'], ['notes', 'یادداشت'], ['finance', 'حساب و درخواست‌ها'], ['chat', 'چت'], ['tasks', 'پیگیری‌ها'], ['activity', 'فعالیت'], ['history', 'تاریخچه']].filter(([key]) => key !== 'finance' || can('financial')).map(([key, label]) => <button key={key} className={detailTab === key ? 'is-active' : ''} onClick={() => setDetailTab(key)}>{label}</button>)}</nav>
   {detailTab === 'overview' && <><div className="crm-kpis">{can('financial') && <><article><span>مانده ریالی</span><strong className={Number(detail.customer.balance_rial) < 0 ? 'crm-debt' : ''}>{crmNumber(detail.customer.balance_rial)}</strong><small>رزرو: {crmNumber(detail.customer.reserved_rial)} · قابل استفاده: {crmNumber(detail.customer.available_rial)}</small></article><article><span>مانده طلایی / گرم</span><strong className={Number(detail.customer.balance_gold) < 0 ? 'crm-debt' : ''}>{crmNumber(detail.customer.balance_gold, true)}</strong><small>رزرو: {crmNumber(detail.customer.reserved_gold, true)} · قابل استفاده: {crmNumber(detail.customer.available_gold, true)}</small></article></>}<article><span>معاملات</span><strong>{crmNumber(detail.customer.trades)}</strong><small>آخرین معامله {crmDate(detail.customer.last_trade_at)}</small></article></div>{can('tasks') && <form className="crm-form-grid" onSubmit={e => {
            e.preventDefault();
            act(async () => {
              await request('put', `/customers/${detail.customer.id}`, {
                ...profile,
                tags: profile.tags.split(/[,،]/).map(t => t.trim()).filter(Boolean)
              });
              await openCustomer(detail.customer.id);
              setSuccess('پرونده به‌روز شد');
            });
          }}><label>مسئول مشتری<select value={profile.owner_id} onChange={e => setProfile(p => ({
                ...p,
                owner_id: e.target.value
              }))}><option value="">بدون مسئول</option>{meta.admins.map(a => <option key={a.id} value={a.id}>{customerName(a)}</option>)}</select></label><label>وضعیت<select value={profile.state} onChange={e => setProfile(p => ({
                ...p,
                state: e.target.value
              }))}>{['normal', 'new', 'active', 'inactive', 'vip', 'needs_help'].map(key => <option key={key} value={key}>{states[key]}</option>)}</select></label><label>برچسب‌ها با ویرگول<input value={profile.tags} onChange={e => setProfile(p => ({
                ...p,
                tags: e.target.value
              }))} /></label><label className="crm-check"><input type="checkbox" checked={profile.marketing_opt_in} onChange={e => setProfile(p => ({
                ...p,
                marketing_opt_in: e.target.checked
              }))} />رضایت دریافت پیام تبلیغاتی دریافت شده است</label><button className="crm-primary" disabled={busy}>ذخیره مشخصات CRM</button></form>}</>}
   {detailTab === 'notes' && <>{can('tasks') && <form onSubmit={e => {
            e.preventDefault();
            act(async () => {
              await request('post', `/customers/${detail.customer.id}/notes`, {
                body: note
              });
              await openCustomer(detail.customer.id);
              setDetailTab('notes');
            });
          }}><label>یادداشت داخلی؛ فقط برای ادمین‌ها<textarea required rows={3} value={note} onChange={e => setNote(e.target.value)} /></label><button disabled={busy} className="crm-primary">ثبت یادداشت</button></form>}<div className="crm-timeline">{detail.notes.map(n => <article key={n.id} className={n.deleted_at ? 'is-deleted' : ''}><strong>{customerName(n)}</strong><p>{n.body}</p><small>{crmDate(n.created_at)}</small>{can('tasks') && <button disabled={busy} onClick={() => act(async () => {
                await request('put', `/notes/${n.id}`, {
                  deleted: !n.deleted_at
                });
                await openCustomer(detail.customer.id);
                setDetailTab('notes');
              })}>{n.deleted_at ? 'بازیابی' : 'حذف با حفظ سابقه'}</button>}</article>)}</div></>}
   {detailTab === 'finance' && can('financial') && <><div className="crm-toolbar"><h3>صورت‌حساب بازه شمسی</h3><div><label>از<input value={dates.start} onChange={e => setDates(d => ({
                  ...d,
                  start: e.target.value
                }))} /></label><label>تا<input value={dates.end} onChange={e => setDates(d => ({
                  ...d,
                  end: e.target.value
                }))} /></label><button onClick={() => act(async () => {
                const {
                  data
                } = await request('get', `/customers/${detail.customer.id}/statement`, {
                  start: gregorianDay(dates.start),
                  end: gregorianDay(dates.end)
                });
                setStatement(data);
                closeDetail();
              })}>تهیه صورت‌حساب</button></div></div><div className="crm-timeline">{detail.transactions.map(t => <article key={t.id}><strong>سند #{t.id} · {t.type}</strong><p>{t.description}</p><small>{crmDate(t.created_at)}</small><div className="crm-delivery-counts"><span>بستانکار ریال: {crmNumber(t.credit_rial)}</span><span>بدهکار ریال: {crmNumber(t.debit_rial)}</span><span>بستانکار طلا: {crmNumber(t.credit_gold, true)}</span><span>بدهکار طلا: {crmNumber(t.debit_gold, true)}</span></div></article>)}</div><h3>درخواست‌های اخیر</h3>{[...detail.wallet.map(r => ({
            ...r,
            kind: 'ریالی'
          })), ...detail.gold.map(r => ({
            ...r,
            kind: 'تحویل طلا'
          }))].map(r => <p key={r.kind + r.id}>#{r.id} · {r.kind} · {states[r.status] || r.status} · {r.reject_reason || ''}</p>)}<Link to="/AdminUserBalances_page">مشاهده تراز کامل کاربران</Link></>}
   {detailTab === 'chat' && <><p className="crm-note">پیام‌های اخیر گفتگو؛ پاسخ جدید از دکمه ارسال پیام ثبت می‌شود. این پیش‌نمایش، پیام را به جای مشتری خوانده‌شده علامت نمی‌زند.</p><button className="crm-primary" onClick={() => composeFor(detail.customer.id)}>شروع یا ادامه گفتگو</button><div className="crm-chat-history">{[...detail.chat].reverse().filter(item => item.message_id).map(msg => <article key={msg.message_id} className={Number(msg.is_admin) ? 'from-admin' : ''}><small>{Number(msg.is_admin) ? 'پشتیبانی' : 'مشتری'} · {crmDate(msg.created_at)}</small><p>{msg.message_text || 'پیوست'}</p>{msg.file_url && <a href={(process.env.REACT_APP_API_URL || '') + msg.file_url} target="_blank" rel="noreferrer">مشاهده پیوست</a>}<small>{Number(msg.is_admin) ? Number(msg.is_read) ? 'دیده‌شده' : 'دیده‌نشده' : ''}</small></article>)}</div></>}
   {detailTab === 'tasks' && <div className="crm-timeline">{detail.tasks.map(t => <article key={t.id}><strong>{t.title}</strong><p>{states[t.status]} · موعد {crmDate(t.due_at)}</p><p>{t.result}</p>{can('tasks') && <button onClick={() => {
              closeDetail();
              setTaskEdit({
                ...t,
                expected_status: t.status,
                result: t.result || '',
                outcome: t.outcome || ''
              });
            }}>مدیریت</button>}</article>)}</div>}
   {detailTab === 'activity' && <div className="crm-timeline">{detail.activity.map(s => <article key={s.id}><strong>{s.current_path}</strong><p>{s.platform} / {s.browser} · {crmDate(s.last_seen_at)}</p><small>فعالیت تقریبی: {crmNumber(s.active_seconds)} ثانیه</small></article>)}</div>}
   {detailTab === 'history' && <div className="crm-timeline">{[...detail.transactions.map(t => ({
            id: 't' + t.id,
            date: t.created_at,
            title: 'سند مالی #' + t.id,
            body: t.description
          })), ...detail.notes.filter(n => !n.deleted_at).map(n => ({
            id: 'n' + n.id,
            date: n.created_at,
            title: 'یادداشت داخلی',
            body: n.body
          })), ...detail.chat.filter(c => c.message_id).map(c => ({
            id: 'c' + c.message_id,
            date: c.created_at,
            title: Number(c.is_admin) ? 'پیام پشتیبانی' : 'پیام مشتری',
            body: c.message_text || 'پیوست'
          })), ...detail.deliveries.map(d => ({
            id: 'd' + d.id,
            date: d.accepted_at || d.created_at,
            title: d.title,
            body: d.rendered_body || states[d.state]
          })), ...detail.audit.map(a => ({
            id: 'a' + a.id,
            date: a.created_at,
            title: a.action,
            body: customerName(a)
          }))].sort((a, b) => new Date(b.date) - new Date(a.date)).map(item => <article key={item.id}><strong>{item.title}</strong><p>{item.body}</p><small>{crmDate(item.date)}</small></article>)}</div>}
  </>}</Dialog>}
  {taskForm && <Dialog title="پیگیری جدید" onClose={closeTask}><form onSubmit={e => {
        e.preventDefault();
        act(async () => {
          await request('post', '/tasks', {
            ...taskForm,
            due_at: dueDate(taskForm.day, taskForm.time)
          });
          setTaskForm(null);
          setSuccess('پیگیری ثبت شد');
          if (tab === 'tasks') await loadTasks();
          if (detail) await openCustomer(detail.customer.id);
        });
      }}><label>شناسه مشتری<input required inputMode="numeric" value={taskForm.user_id} onChange={e => setTaskForm(f => ({
            ...f,
            user_id: e.target.value
          }))} /></label><label>عنوان<input required maxLength={200} value={taskForm.title} onChange={e => setTaskForm(f => ({
            ...f,
            title: e.target.value
          }))} /></label><label>توضیحات<textarea rows={3} value={taskForm.body} onChange={e => setTaskForm(f => ({
            ...f,
            body: e.target.value
          }))} /></label><div className="crm-form-grid"><label>تاریخ شمسی<input required placeholder="۱۴۰۵/۰۷/۱۶" value={taskForm.day} onChange={e => setTaskForm(f => ({
              ...f,
              day: e.target.value
            }))} /></label><label>ساعت<input required placeholder="۱۰:۳۰" value={taskForm.time} onChange={e => setTaskForm(f => ({
              ...f,
              time: e.target.value
            }))} /></label><label>مسئول<select value={taskForm.owner_id} onChange={e => setTaskForm(f => ({
              ...f,
              owner_id: e.target.value
            }))}>{meta.admins.map(a => <option key={a.id} value={a.id}>{customerName(a)}</option>)}</select></label><label>اولویت<select value={taskForm.priority} onChange={e => setTaskForm(f => ({
              ...f,
              priority: e.target.value
            }))}><option value="normal">عادی</option><option value="high">فوری</option><option value="low">کم</option></select></label></div><button className="crm-primary" disabled={busy}>ثبت پیگیری</button></form></Dialog>}
  {taskEdit && <Dialog title={taskEdit.title} onClose={closeTaskEdit}><form onSubmit={e => {
        e.preventDefault();
        act(async () => {
          await request('put', `/tasks/${taskEdit.id}`, {
            ...taskEdit,
            deleted: Boolean(taskEdit.deleted ?? taskEdit.deleted_at),
            ...(taskEdit.day ? {
              due_at: dueDate(taskEdit.day, taskEdit.time)
            } : {})
          });
          setTaskEdit(null);
          setSuccess('پیگیری به‌روز شد');
          if (tab === 'tasks') await loadTasks();
          if (detail) await openCustomer(detail.customer.id);
        });
      }}><label>وضعیت<select value={taskEdit.status} onChange={e => setTaskEdit(t => ({
            ...t,
            status: e.target.value
          }))}>{['open', 'progress', 'done', 'canceled'].map(key => <option key={key} value={key}>{states[key]}</option>)}</select></label><label>مسئول<select value={taskEdit.owner_id} onChange={e => setTaskEdit(t => ({
            ...t,
            owner_id: e.target.value
          }))}>{meta.admins.map(a => <option key={a.id} value={a.id}>{customerName(a)}</option>)}</select></label><div className="crm-form-grid"><label>موعد جدید / تاریخ شمسی<input placeholder="خالی: بدون تغییر" value={taskEdit.day || ''} onChange={e => setTaskEdit(t => ({
              ...t,
              day: e.target.value
            }))} /></label><label>ساعت جدید<input placeholder="۱۰:۳۰" value={taskEdit.time || ''} onChange={e => setTaskEdit(t => ({
              ...t,
              time: e.target.value
            }))} /></label></div><label className="crm-check"><input type="checkbox" checked={Boolean(taskEdit.deleted ?? taskEdit.deleted_at)} onChange={e => setTaskEdit(t => ({
            ...t,
            deleted: e.target.checked
          }))} />حذف پیگیری با حفظ سابقه؛ برداشتن تیک برای بازیابی</label><label>نتیجه<select value={taskEdit.outcome} onChange={e => setTaskEdit(t => ({
            ...t,
            outcome: e.target.value
          }))}><option value="">انتخاب نتیجه</option>{['پاسخ نداد', 'نیازمند راهنمایی', 'مشکل حل شد', 'تماس در زمان دیگر', 'پیگیری مالی انجام شد'].map(value => <option key={value}>{value}</option>)}</select></label><label>شرح نتیجه<textarea required={taskEdit.status === 'done'} rows={3} value={taskEdit.result} onChange={e => setTaskEdit(t => ({
            ...t,
            result: e.target.value
          }))} /></label><button disabled={busy} className="crm-primary">ذخیره نتیجه</button></form></Dialog>}
  {campaignDetail && <Dialog title={`جزئیات ارسال #${campaignDetail.campaign.id}`} onClose={closeCampaign} wide><div className="crm-timeline">{campaignDetail.items.map(d => <article key={d.id}><strong>{customerName(d)}</strong><span className="crm-badge">{states[d.state]}</span><p>{d.rendered_body}</p><p className="crm-debt">{d.error_text}</p><small>{crmDate(d.accepted_at)} {d.chat_read != null ? Number(d.chat_read) ? '· پیام چت دیده‌شده' : '· پیام چت دیده‌نشده' : ''}</small></article>)}</div></Dialog>}
  {statement && <Dialog title="صورت‌حساب قابل چاپ" onClose={closeStatement}><p>{customerName(statement.customer)} · {crmNumber(statement.items.length)} سند</p><p>مانده ابتدای بازه: {crmNumber(statement.opening.rial)} ریال / {crmNumber(statement.opening.gold, true)} گرم</p><button className="crm-primary" onClick={() => downloadFile(`statement-${statement.customer.id}.html`, statementHtml(statement), 'text/html;charset=utf-8')}><Download size={17} />دریافت صورت‌حساب و چاپ / PDF</button><button disabled={busy} onClick={() => act(async () => {
        const {
          data
        } = await request('post', '/statements', {
          user_id: statement.customer.id,
          start: statement.range.start,
          end: statement.range.end
        });
        setSuccess(`صورت‌حساب شماره ${data.id} در حساب مشتری قرار گرفت و اعلان داخلی ایجاد شد`);
        setStatement(null);
      })}>قرار دادن صورت‌حساب در حساب مشتری و ارسال اعلان</button><p className="crm-note">فایل صورت‌حساب حاوی اطلاعات مالی است. در فایل، گزینه چاپ و ذخیره PDF وجود دارد.</p></Dialog>}
  {busy && <div className="crm-busy" role="status">در حال پردازش…</div>}
 </section>;
}
